import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { date, dateTime, humanise, num, rupees } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Empty, Field, Modal, SearchInput, Select, StatusBadge, Textarea, useToast } from '../components/ui';

interface ListingRow {
  id: string;
  title: string;
  status: string;
  priceInr: string | null;
  images: string[];
  viewCount: number;
  boostedUntil: string | null;
  liveAt: string | null;
  expiresAt: string | null;
  rejectReason: string | null;
  createdAt: string;
  category: { id: string; name: string };
  location: { id: string; name: string } | null;
  seller: { id: string; mobile: string; displayName: string | null; kycStatus: string };
}

interface ListingDetail extends ListingRow {
  description: string;
  address: string;
  isNegotiable: boolean;
  lat: number | null;
  lng: number | null;
  attributes: Array<{ key: string; valueText: string | null; valueNum: number | null; valueBool: boolean | null }>;
  payment: { id: string; amountPaise: number; status: string; capturedAt: string | null } | null;
}

/** Covers every listing type — products, property, services, jobs, rentals. */
export function ListingsPage() {
  const { get, set } = useListParams();
  const [openId, setOpenId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const search = get('search');
  const status = get('status');

  const detail = useQuery({
    queryKey: ['listing', openId],
    queryFn: () => api.get<ListingDetail>(`/admin/listings/${openId}`),
    enabled: Boolean(openId),
  });

  const moderate = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: string }) =>
      api.patch(`/admin/listings/${id}/moderate`, { status, reason }),
    onSuccess: () => {
      toast('Listing updated', 'success');
      setRejecting(false);
      setReason('');
      setOpenId(null);
      qc.invalidateQueries({ queryKey: ['listings'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<ListingRow>> = [
    {
      key: 'listing',
      header: 'Listing',
      render: (r) => (
        <div className="row">
          {r.images[0] ? (
            <img className="thumb" src={r.images[0]} alt="" loading="lazy" />
          ) : (
            <div className="thumb" style={{ display: 'grid', placeItems: 'center', color: 'var(--ink-faint)' }}>▤</div>
          )}
          <div style={{ minWidth: 0 }}>
            <div className="cell-strong cell-clip">{r.title}</div>
            <div className="cell-muted">
              {r.category.name}
              {r.location && ` · ${r.location.name}`}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'seller',
      header: 'Seller',
      render: (r) => (
        <Link to={`/users/${r.seller.id}`} onClick={(e) => e.stopPropagation()}>
          {r.seller.displayName ?? r.seller.mobile}
        </Link>
      ),
    },
    { key: 'price', header: 'Price', numeric: true, render: (r) => rupees(r.priceInr) },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div className="row" style={{ gap: 5 }}>
          <StatusBadge value={r.status} />
          {r.boostedUntil && new Date(r.boostedUntil) > new Date() && <Badge tone="orange">Boosted</Badge>}
        </div>
      ),
    },
    { key: 'views', header: 'Views', numeric: true, render: (r) => num(r.viewCount) },
    { key: 'expires', header: 'Expires', render: (r) => <span className="cell-muted">{date(r.expiresAt)}</span> },
    { key: 'created', header: 'Created', render: (r) => <span className="cell-muted">{date(r.createdAt)}</span> },
  ];

  const d = detail.data;

  return (
    <>
      <DataTable<ListingRow>
        queryKey={['listings', search, status]}
        path="/admin/listings"
        params={{ search, status }}
        columns={columns}
        onRowClick={(r) => setOpenId(r.id)}
        emptyIcon="▤"
        emptyTitle="No listings match"
        toolbar={
          <>
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Search titles…" />
            <Select
              value={status}
              placeholder="Any status"
              onChange={(e) => set('status', e.target.value)}
              options={['DRAFT', 'PENDING_PAYMENT', 'LIVE', 'HOLD', 'EXPIRED', 'REJECTED', 'DELETED'].map((v) => ({
                value: v,
                label: humanise(v),
              }))}
            />
          </>
        }
      />

      {openId && (
        <Modal
          wide
          title={d?.title ?? 'Listing'}
          sub={d ? `${d.category.name} · ${d.address}` : undefined}
          onClose={() => {
            setOpenId(null);
            setRejecting(false);
          }}
          footer={
            d && (
              <>
                <Button onClick={() => setOpenId(null)}>Close</Button>
                {d.status !== 'DELETED' && (
                  <Button variant="danger" onClick={() => moderate.mutate({ id: d.id, status: 'DELETED' })}>
                    Delete
                  </Button>
                )}
                {d.status !== 'REJECTED' && (
                  <Button variant="ghost" onClick={() => setRejecting(true)}>
                    Reject…
                  </Button>
                )}
                {d.status !== 'LIVE' && d.status !== 'DELETED' && (
                  <Button variant="success" onClick={() => moderate.mutate({ id: d.id, status: 'LIVE' })}>
                    Publish
                  </Button>
                )}
              </>
            )
          }
        >
          {detail.isLoading || !d ? (
            <div className="skeleton" style={{ height: 240 }} />
          ) : rejecting ? (
            <Field label="Why is this being rejected?" hint="Shown to the seller.">
              <Textarea value={reason} autoFocus onChange={(e) => setReason(e.target.value)} />
              <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                <Button onClick={() => setRejecting(false)}>Back</Button>
                <Button
                  variant="danger"
                  disabled={!reason.trim()}
                  loading={moderate.isPending}
                  onClick={() => moderate.mutate({ id: d.id, status: 'REJECTED', reason: reason.trim() })}
                >
                  Reject listing
                </Button>
              </div>
            </Field>
          ) : (
            <div className="stack">
              {d.images.length > 0 && (
                <div className="row" style={{ gap: 8, overflowX: 'auto' }}>
                  {d.images.map((src) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      style={{ width: 116, height: 88, objectFit: 'cover', borderRadius: 10, flex: '0 0 auto' }}
                    />
                  ))}
                </div>
              )}

              <div className="kv">
                <span className="kv__k">Status</span>
                <span className="kv__v">
                  <StatusBadge value={d.status} />
                  {d.rejectReason && <span style={{ color: 'var(--danger)', marginLeft: 8 }}>{d.rejectReason}</span>}
                </span>
                <span className="kv__k">Price</span>
                <span className="kv__v">
                  {rupees(d.priceInr)}
                  {d.isNegotiable && <span className="cell-muted"> · negotiable</span>}
                </span>
                <span className="kv__k">Seller</span>
                <span className="kv__v">
                  <Link to={`/users/${d.seller.id}`}>{d.seller.displayName ?? d.seller.mobile}</Link>{' '}
                  <StatusBadge value={d.seller.kycStatus} />
                </span>
                <span className="kv__k">Address</span>
                <span className="kv__v">{d.address}</span>
                <span className="kv__k">Coordinates</span>
                <span className="kv__v">{d.lat && d.lng ? `${d.lat.toFixed(5)}, ${d.lng.toFixed(5)}` : '—'}</span>
                <span className="kv__k">Views</span>
                <span className="kv__v">{num(d.viewCount)}</span>
                <span className="kv__k">Live since</span>
                <span className="kv__v">{dateTime(d.liveAt)}</span>
                <span className="kv__k">Expires</span>
                <span className="kv__v">{dateTime(d.expiresAt)}</span>
                {d.payment && (
                  <>
                    <span className="kv__k">Payment</span>
                    <span className="kv__v">
                      <StatusBadge value={d.payment.status} /> {dateTime(d.payment.capturedAt)}
                    </span>
                  </>
                )}
              </div>

              <div>
                <div className="field__label" style={{ marginBottom: 6 }}>
                  Description
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{d.description}</div>
              </div>

              {d.attributes.length > 0 && (
                <div>
                  <div className="field__label" style={{ marginBottom: 6 }}>
                    Attributes
                  </div>
                  <div className="row row--wrap" style={{ gap: 6 }}>
                    {d.attributes.map((a) => (
                      <Badge key={a.key} tone="blue">
                        {a.key}: {a.valueText ?? a.valueNum ?? String(a.valueBool)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}

      {!openId && detail.isError && <Empty icon="⚠️" title="Could not load that listing" />}
    </>
  );
}
